import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { X, Loader2, HelpCircle, ZoomIn, ZoomOut } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { AnnotationLayer } from '../pdf/AnnotationLayer';
import { MarkupLayer, type MarkupCommitPayload } from '../pdf/MarkupLayer';
import { ViewerModeBar, type ViewerInteractionMode } from '../pdf/ViewerModeBar';
import { useMarkupStrokeWidth } from '../pdf/markupStrokeWidth';
import type { CommentMention, DocumentMarkupTool, PortalDeliverableApproval } from '../../types';
import { pdfPageStepFromArrowKey } from '../../utils/pdfPageArrowKeys';
import {
  destroyPortalPdfMarkupTour,
  isPortalPdfMarkupTourActive,
  startPortalPdfMarkupTour,
} from './PortalPdfMarkupTour';
import {
  logPortalActivity,
  usePortalPdfEngagementSession,
} from '../../utils/portalActivityLogger';
import {
  subscribePortalAnnotations,
  createPortalAnnotation,
  addPortalAnnotationReply,
  updatePortalAnnotationPosition,
  deletePortalAnnotation,
  portalAnnotationDocumentId,
} from '../../utils/portalPdfAnnotationService';
import {
  subscribePortalMarkup,
  createPortalMarkupStroke,
  deletePortalMarkupStroke,
  portalMarkupStrokeToDocumentStroke,
  portalAnnotationToPdfAnnotation,
} from '../../utils/portalDocumentMarkupService';
import {
  notifyPortalAnnotationCreated,
  resolvePortalPmNotifyUserIds,
} from '../../utils/portalNotificationService';
import { resolvePortalMarkupAuthorName } from '../../utils/portalMarkupAuthor';
import { canDeletePortalPdfAnnotation } from '../../utils/commentPermissions';
import { usePreventViewerHistoryBack } from '../../hooks/usePreventViewerHistoryBack';
import { formatPortalApprovalCaption } from '../../utils/portalDeliverableApprovalService';
import '../pdf/PdfViewerModal.css';
import './PortalPdfViewerModal.css';
import './PortalPdfMarkupTour.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Zoom is a multiple of fit-to-width. 1 fills the viewer. */
const PORTAL_PDF_ZOOM_MIN = 0.5;
const PORTAL_PDF_ZOOM_MAX = 4;
const PORTAL_PDF_ZOOM_STEP = 1.35;

function clampPortalPdfZoom(zoom: number): number {
  return Math.max(PORTAL_PDF_ZOOM_MIN, Math.min(PORTAL_PDF_ZOOM_MAX, zoom));
}

export interface PortalPdfViewerModalProps {
  url: string;
  title: string;
  portalId: string;
  dealId: string;
  releaseId: string;
  bundleId?: string;
  projectName?: string;
  /** Staff previewing the portal vs client office visitors. */
  isStaffPortalUser?: boolean;
  /** Company / client label shown on markup from non-staff sessions. */
  clientDisplayName?: string;
  /** Run the first-time markup walkthrough after the PDF loads. */
  startTourOnOpen?: boolean;
  activitySource?: 'client' | 'employee' | 'stakeholder';
  /** Shown under the title when this version has already been signed. */
  approval?: PortalDeliverableApproval | null;
  /** Present when this file is one of several attached to the same approval. */
  onAdvanceToNextFile?: () => void;
  /** Present on the last file of an approval, opens the initials modal. */
  onApprove?: () => void;
  onClose: () => void;
}

const PortalPdfViewerModal: React.FC<PortalPdfViewerModalProps> = ({
  url,
  title,
  portalId,
  dealId,
  releaseId,
  bundleId,
  projectName,
  isStaffPortalUser = false,
  clientDisplayName,
  startTourOnOpen = false,
  activitySource = 'client',
  approval,
  onAdvanceToNextFile,
  onApprove,
  onClose,
}) => {
  const approvalCaption = formatPortalApprovalCaption(approval);
  const { user } = useAuth();
  usePreventViewerHistoryBack(true);
  const documentId = portalAnnotationDocumentId(releaseId);
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  usePortalPdfEngagementSession(portalId, dealId, activitySource, true, {
    releaseId,
    documentId,
    documentTitle: title,
    context: 'portal_pdf_viewer',
  });

  const markupAuthorName = useMemo(
    () =>
      resolvePortalMarkupAuthorName({
        isStaffPortalUser,
        userName: user?.name,
        userEmail: user?.email,
        clientDisplayName: clientDisplayName ?? projectName,
      }),
    [isStaffPortalUser, user?.name, user?.email, clientDisplayName, projectName],
  );

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setPageNumber(1);
    setNumPages(0);
    setZoom(1);
    setLoading(true);
    setError(null);
  }, [url]);
  const [annotations, setAnnotations] = useState<ReturnType<typeof portalAnnotationToPdfAnnotation>[]>([]);
  const [markupStrokes, setMarkupStrokes] = useState<ReturnType<typeof portalMarkupStrokeToDocumentStroke>[]>([]);
  const [viewerMode, setViewerMode] = useState<ViewerInteractionMode>('comment');
  const [drawTool, setDrawTool] = useState<DocumentMarkupTool>('pen');
  const [drawColor, setDrawColor] = useState('#3b82f6');
  const { strokeWidth: drawStrokeWidth, setStrokeWidth: setDrawStrokeWidth } = useMarkupStrokeWidth(drawTool);
  const tourStartedRef = useRef(false);

  useEffect(() => {
    if (!startTourOnOpen || loading || tourStartedRef.current) return;
    tourStartedRef.current = true;
    const t = window.setTimeout(() => {
      startPortalPdfMarkupTour({ hasPagination: numPages > 1 });
    }, 600);
    return () => window.clearTimeout(t);
  }, [startTourOnOpen, loading, numPages]);

  useEffect(() => {
    const unsub = subscribePortalAnnotations(documentId, rows =>
      setAnnotations(rows.map(portalAnnotationToPdfAnnotation)),
    );
    return unsub;
  }, [documentId]);

  useEffect(() => {
    const unsub = subscribePortalMarkup(documentId, rows =>
      setMarkupStrokes(rows.map(portalMarkupStrokeToDocumentStroke)),
    );
    return unsub;
  }, [documentId]);

  const goPrevPage = useCallback(() => setPageNumber(page => Math.max(1, page - 1)), []);
  const goNextPage = useCallback(
    () => setPageNumber(page => Math.min(numPages || page, page + 1)),
    [numPages],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const step = pdfPageStepFromArrowKey(e);
      if (step) {
        e.preventDefault();
        if (step < 0) goPrevPage();
        else goNextPage();
        return;
      }
      if (e.key !== 'Escape') return;
      if (isPortalPdfMarkupTourActive()) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNextPage, goPrevPage, onClose]);

  useEffect(() => () => destroyPortalPdfMarkupTour(), []);

  useEffect(() => {
    const el = canvasWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setPageWidth(w - 32);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = canvasWrapRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      const dy = Math.max(-80, Math.min(80, event.deltaY));
      const factor = Math.exp(-dy * 0.005);
      setZoom(current => clampPortalPdfZoom(current * factor));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const zoomIn = useCallback(() => setZoom(current => clampPortalPdfZoom(current * PORTAL_PDF_ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setZoom(current => clampPortalPdfZoom(current / PORTAL_PDF_ZOOM_STEP)), []);
  const zoomToFit = useCallback(() => setZoom(1), []);

  const onDocumentLoadSuccess = (pdf: { numPages: number }) => {
    setNumPages(pdf.numPages);
    setLoading(false);
  };

  const pageAnnotations = useMemo(
    () => annotations.filter(a => a.pageNumber === pageNumber),
    [annotations, pageNumber],
  );
  const pageStrokes = useMemo(
    () => markupStrokes.filter(s => s.pageNumber === pageNumber),
    [markupStrokes, pageNumber],
  );

  const annotationInteractive = viewerMode === 'comment';
  const markupInteractive = viewerMode === 'draw';

  const handleAddAnnotation = async (
    pn: number,
    x: number,
    y: number,
    content: string,
    mentions?: CommentMention[],
  ) => {
    if (!user?.id) return;
    await createPortalAnnotation({
      portalId,
      dealId,
      releaseId,
      bundleId,
      documentId,
      documentUrl: url,
      pageNumber: pn,
      x,
      y,
      content,
      authorUid: user.id,
      authorName: markupAuthorName,
      mentions,
    });
    void logPortalActivity(
      portalId,
      dealId,
      'action',
      'pdf_comment_added',
      { releaseId, documentId, pageNumber: pn, documentTitle: title },
      activitySource,
    );
    const notifyIds = await resolvePortalPmNotifyUserIds(dealId);
    if (notifyIds.length > 0) {
      await notifyPortalAnnotationCreated({
        portalId,
        dealId,
        releaseId,
        authorName: markupAuthorName,
        projectName,
        notifyUserIds: notifyIds,
      });
    }
  };

  const handleAddReply = async (annotationId: string, content: string, mentions?: CommentMention[]) => {
    if (!user?.id) return;
    await addPortalAnnotationReply(annotationId, {
      userId: user.id,
      userName: markupAuthorName,
      content,
      mentions,
    });
  };

  const handleMarkupCommit = async (payload: MarkupCommitPayload) => {
    if (!user?.id) return;
    try {
      await createPortalMarkupStroke({
        portalId,
        dealId,
        releaseId,
        bundleId,
        documentId,
        documentUrl: url,
        pageNumber: payload.pageNumber,
        tool: payload.tool,
        color: payload.color,
        strokeWidth: payload.strokeWidth,
        points: payload.points,
        authorUid: user.id,
        authorName: markupAuthorName,
      });
      void logPortalActivity(
        portalId,
        dealId,
        'action',
        'pdf_markup_added',
        {
          releaseId,
          documentId,
          pageNumber: payload.pageNumber,
          tool: payload.tool,
          documentTitle: title,
        },
        activitySource,
      );
    } catch (err) {
      console.warn('[PortalPdfViewer] Failed to save markup stroke', err);
    }
  };

  const visualWidth = Math.max(1, Math.round((pageWidth ?? 600) * zoom));

  return (
    <div className="portal-pdf-viewer-overlay" role="presentation">
      <div className="portal-pdf-viewer-shell" role="dialog" aria-modal="true" aria-label={title}>
        <header className="portal-pdf-viewer-header">
          <div className="portal-pdf-viewer-heading">
            <h2>{title}</h2>
            {approvalCaption ? <p className="portal-pdf-viewer-approval">{approvalCaption}</p> : null}
          </div>
          <div className="portal-pdf-viewer-tools">
            <div className="portal-pdf-viewer-zoom" role="group" aria-label="Zoom">
              <button type="button" onClick={zoomOut} disabled={zoom <= PORTAL_PDF_ZOOM_MIN + 0.001} aria-label="Zoom out" title="Zoom out">
                <ZoomOut size={16} />
              </button>
              <button type="button" onClick={zoomToFit} aria-label="Fit to width" title="Fit to width">
                {Math.round(zoom * 100)}%
              </button>
              <button type="button" onClick={zoomIn} disabled={zoom >= PORTAL_PDF_ZOOM_MAX - 0.001} aria-label="Zoom in" title="Zoom in">
                <ZoomIn size={16} />
              </button>
            </div>
            <button
              type="button"
              className="portal-pdf-viewer-help-btn"
              data-tour="portal-pdf-help"
              onClick={e => {
                e.stopPropagation();
                startPortalPdfMarkupTour({ hasPagination: numPages > 1, force: true });
              }}
              aria-label="Show markup help tour"
              title="Show help tour"
            >
              <HelpCircle size={18} />
            </button>
            <button type="button" className="portal-pdf-viewer-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </header>

        <ViewerModeBar
          mode={viewerMode}
          onModeChange={setViewerMode}
          drawTool={drawTool}
          onDrawToolChange={setDrawTool}
          drawColor={drawColor}
          onDrawColorChange={setDrawColor}
          drawStrokeWidth={drawStrokeWidth}
          onDrawStrokeWidthChange={setDrawStrokeWidth}
          hideMeasure
          tourAnchors
        />

        <div className="portal-pdf-viewer-main" ref={canvasWrapRef}>
          {loading && (
            <div className="portal-pdf-viewer-loading">
              <Loader2 size={24} className="cps-spinner" /> Loading PDF…
            </div>
          )}
          {error && <div className="portal-pdf-viewer-error">{error}</div>}
          <Document
            key={url}
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={err => {
              setError(err.message || 'Could not load PDF');
              setLoading(false);
            }}
            loading={null}
          >
            <div
              className="portal-pdf-page-wrap"
              style={{ width: visualWidth }}
              data-tour="portal-pdf-canvas"
            >
              <Page
                pageNumber={pageNumber}
                width={visualWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
              <div
                className="portal-pdf-markup-layer"
                style={{
                  pointerEvents: markupInteractive ? 'auto' : 'none',
                }}
              >
                <MarkupLayer
                  pageNumber={pageNumber}
                  scale={1}
                  strokes={pageStrokes}
                  activeTool={drawTool}
                  color={drawColor}
                  baseStrokeWidth={drawStrokeWidth}
                  enabled={markupInteractive}
                  currentUserId={user?.id}
                  onCommitStroke={handleMarkupCommit}
                  onDeleteStroke={async strokeId => {
                    const stroke = pageStrokes.find(s => s.id === strokeId);
                    if (stroke && user?.id && stroke.userId === user.id) {
                      await deletePortalMarkupStroke(strokeId);
                    }
                  }}
                />
              </div>
              <div
                className="portal-pdf-annotation-layer"
                style={{
                  pointerEvents: annotationInteractive ? 'auto' : 'none',
                }}
              >
                <AnnotationLayer
                  pageNumber={pageNumber}
                  scale={1}
                  annotations={pageAnnotations}
                  loading={false}
                  interactive={annotationInteractive}
                  pinModeActive={false}
                  onAddAnnotation={handleAddAnnotation}
                  onAddReply={handleAddReply}
                  onMoveAnnotation={updatePortalAnnotationPosition}
                  onDeleteAnnotationById={deletePortalAnnotation}
                  onDeleteAnnotation={() => {}}
                  notifiesWithoutMention
                  canDeleteAnnotation={ann => canDeletePortalPdfAnnotation(user, ann.userId)}
                  currentUserId={user?.id}
                />
              </div>
            </div>
          </Document>
        </div>

        {(numPages > 1 || onAdvanceToNextFile || onApprove) && (
          <footer className="portal-pdf-viewer-footer" data-tour="portal-pdf-pagination">
            {numPages > 1 ? (
              <div className="portal-pdf-viewer-pages">
                <button
                  type="button"
                  disabled={pageNumber <= 1}
                  onClick={goPrevPage}
                >
                  Previous
                </button>
                <span>
                  Page {pageNumber} of {numPages}
                </span>
                <button
                  type="button"
                  disabled={pageNumber >= numPages}
                  onClick={goNextPage}
                >
                  Next
                </button>
              </div>
            ) : null}
            {onAdvanceToNextFile ? (
              <button type="button" className="portal-pdf-viewer-advance" onClick={onAdvanceToNextFile}>
                Advance to next file
              </button>
            ) : null}
            {onApprove ? (
              <button type="button" className="portal-pdf-viewer-advance" onClick={onApprove}>
                Approve
              </button>
            ) : null}
          </footer>
        )}
      </div>
    </div>
  );
};

export default PortalPdfViewerModal;
